import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../../../context/AuthContext";
import { oneKlicks } from "../../../SDK/oneclick";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import { Badge } from "react-bootstrap";

const STATUS_OPTIONS = [
    { label: "All Claims", value: "all" },
    { label: "Pending", value: "pending" },
    { label: "Under Review", value: "under_review" },
    { label: "Approved", value: "approved" },
    { label: "Rejected", value: "rejected" },
];

const Claims = () => {
    const { userData } = useAuth();
    const [claims, setClaims] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState("all");

    const hasCompanyScope = useMemo(() => {
        const companyId = userData?.company_id || userData?.companyId || userData?.company;
        return Boolean(companyId);
    }, [userData]);

    useEffect(() => {
        fetchClaims();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter, userData?.id, userData?.company_id]);

    const fetchClaims = async () => {
        if (!userData?.id) {
            setClaims([]);
            return;
        }

        try {
            setLoading(true);
            const tableName = "insurance_claims"; // existing OneClick table
            const conditions = {};

            if (filter !== "all") {
                conditions.status = filter;
            }

            // Priority 1: Filter by company_id if user belongs to a company
            const companyId = userData?.company_id || userData?.companyId || userData?.company;
            if (companyId) {
                conditions.company_id = companyId;
            }
            // Priority 2: Filter by agent_id if user is an agent
            else if (userData?.agent_id || userData?.agentId) {
                conditions.agent_id = userData.agent_id || userData.agentId;
            }
            // Priority 3: Filter by user_id for individual users
            else {
                conditions.user_id = userData.id;
            }

            console.log("Fetching claims with conditions:", conditions);

            const response = await oneKlicks.read(
                "",
                tableName,
                conditions,
                "created_at DESC",
                "",
                "",
                ""
            );

            if (response?.success && Array.isArray(response?.data)) {
                const normalised = response.data.map((claim) => {
                    let documents = claim.documents;
                    if (typeof documents === "string") {
                        try {
                            documents = JSON.parse(documents);
                        } catch (err) {
                            documents = [];
                        }
                    }

                    return {
                        id: claim.id,
                        claimNumber: claim.claim_number || `CLM-${claim.id}`,
                        policyId: claim.policy_id,
                        customerId: claim.customer_id,
                        claimAmount: claim.claim_amount ?? 0,
                        approvedAmount: claim.approved_amount ?? null,
                        status: claim.status || "pending",
                        claimReason: claim.claim_reason,
                        claimDate: claim.claim_date,
                        incidentDate: claim.incident_date,
                        documents: documents || [],
                        createdAt: claim.created_at,
                        updatedAt: claim.updated_at,
                    };
                });

                setClaims(normalised);
            } else {
                setClaims([]);
            }
        } catch (error) {
            console.error("Error fetching claims:", error);
            toast.error("Failed to load claims");
            setClaims([]);
        } finally {
            setLoading(false);
        }
    };

    const handleFileNewClaim = () => {
        window.location.href = "/insurance/claims/new";
    };

    const renderStatusBadge = (status) => {
        const normalized = (status || "").toLowerCase();
        const label = {
            pending: "Pending",
            under_review: "Under Review",
            approved: "Approved",
            rejected: "Rejected",
            partially_settled: "Partially Settled",
        }[normalized] || status || "Pending";

        const badgeClass = {
            approved: "badge-success",
            rejected: "badge-danger",
            under_review: "badge-info",
            partially_settled: "badge-primary",
        }[normalized] || "badge-warning";

        return <span className={`badge ${badgeClass}`}>{label}</span>;
    };

    return (
        <div className="container-fluid py-3">
            <div className="card mb-5">
                <div className="card-body">
                    <div className="align-items-center justify-content-between mb-3">
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <h5 className="mb-0 font-weight-bold mr-3">Claims Management</h5>
                            <Badge bg="info">Claims</Badge>
                        </div>
                        <div className="d-flex justify-content-between align-items-center" style={{ gap: 12 }}>
                            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", flex: 1 }}>
                                {STATUS_OPTIONS.map((option) => (
                                    <button
                                        key={option.value}
                                        className={`btn ${filter === option.value ? "btn-primary" : "btn-outline-primary"}`}
                                        onClick={() => setFilter(option.value)}
                                        style={{ fontSize: '14px', padding: '6px 12px' }}
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </div>
                            <button className="btn btn-primary" onClick={handleFileNewClaim} style={{ whiteSpace: 'nowrap' }}>
                                + File New Claim
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card">
                <div className="card-body">
                    {loading ? (
                        <div className="text-center p-5">
                            <div className="spinner-border" role="status">
                                <span className="sr-only">Loading...</span>
                            </div>
                        </div>
                    ) : claims.length > 0 ? (
                        <div className="table-responsive">
                            <table hover size="sm" className="mb-0 align-middle">
                                <thead>
                                    <tr>
                                        <th>Claim Number</th>
                                        <th>Policy ID</th>
                                        <th>Customer ID</th>
                                        <th>Claim Amount</th>
                                        <th>Claim Date</th>
                                        <th>Status</th>
                                        <th style={{ width: '150px' }}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {claims.map((claim) => (
                                        <tr key={claim.id}>
                                            <td data-label="Claim Number">{claim.claimNumber}</td>
                                            <td data-label="Policy ID">{claim.policyId ?? "—"}</td>
                                            <td data-label="Customer ID">{claim.customerId ?? "—"}</td>
                                            <td data-label="Claim Amount">
                                                ₹{Number(claim.claimAmount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                            </td>
                                            <td data-label="Claim Date">
                                                {claim.claimDate
                                                    ? new Date(claim.claimDate).toLocaleDateString("en-IN", {
                                                          year: "numeric",
                                                          month: "short",
                                                          day: "numeric",
                                                      })
                                                    : claim.createdAt
                                                    ? new Date(claim.createdAt).toLocaleDateString("en-IN", {
                                                          year: "numeric",
                                                          month: "short",
                                                          day: "numeric",
                                                      })
                                                    : "—"}
                                            </td>
                                            <td data-label="Status" style={{ textAlign: 'center' }}>
                                                {renderStatusBadge(claim.status)}
                                            </td>
                                            <td data-label="Action">
                                                <div style={{ display: 'flex', flexDirection: 'row', gap: '6px', alignItems: 'center', flexWrap: 'nowrap', justifyContent: 'flex-end' }}>
                                                    <Link 
                                                        to={`/insurance/claims/${claim.id}`} 
                                                        className="btn btn-sm btn-primary fit-content"
                                                        style={{ fontSize: '12px', padding: '4px 12px' }}
                                                    >
                                                        <i className="fa fa-eye" style={{ fontSize: '11px', marginRight: '4px' }}></i>
                                                        View
                                                    </Link>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-center p-5">
                            <div className="dc-emptydata-holder">
                                <div className="dc-emptydata">
                                    <div className="dc-emptydetails dc-empty-booking dc-emptyholder-sm">
                                        <span></span>
                                        <em>No claims found</em>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Claims;
